import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Portal } from 'react-portal'
import { useCallback, useFeatures, useMedia } from 'sb-hooks'
import { mixpanel, constants } from 'common/helpers'
import { injectIntl } from 'sb-react-intl'
import cx from 'classnames'

import Box from 'components/_new/layout/Box/Box'
import Text from 'components/_new/typography/Text/Text'
import Modal from 'components/_new/modal/Modal/Modal'
import Title from 'components/_new/typography/deprecated/Title/Title'
import Button from 'components/_new/Button/Button'
import Overlay from 'components/_new/layout/Overlay/Overlay'
import ButtonsInRow from 'components/_new/buttons/ButtonsInRow/ButtonsInRow'
import ModalContainer from 'components/_new/modal/ModalContainer/ModalContainer'
import ContentWithLoader from 'components/loaders/ContentWithLoader/ContentWithLoader'

import Header from './Header/Header'
import OrderInfo from './OrderInfo/OrderInfo'

import messages from './messages'
import s from './SelectedPlanSummaryModal.scss'

import { useSelectedPlan } from './util'

import birdieImage from './images/birdie.svg'


const SelectedPlanSummaryModal = ({ plan, intl, onClose, redirectLink }) => {
  const { isMobile } = useMedia()
  const { [constants.features.upgradeActivationDateInSummary]: isActivationDateInSummaryEnabled } = useFeatures()
  const {
    isFetching, isSuccess, billItems, total,
    upgradeActivationMonth, nextChargeMonth,
    handleContinueClick: _handleContinueClick,
  } = useSelectedPlan({ plan, intl, onClose })

  const isChargeInfoVisible = isActivationDateInSummaryEnabled && (upgradeActivationMonth || nextChargeMonth)

  const handleContinueClick = useCallback(() => {
    mixpanel.track(constants.analyticsEvents.newThankYouPage.upgradeContinueClick)
    _handleContinueClick()
  }, [ _handleContinueClick ])

  const modalClassName = cx({
    [s.successModal]: isSuccess,
  })

  return (
    <Portal>
      <ModalContainer>
        <Modal className={modalClassName}>
          <ContentWithLoader isFetching={isFetching}>
            {
              isSuccess ? (
                <Box mt={isMobile ? '48' : '24'} alignCenter>
                  <img src={birdieImage} alt="" />
                  <Box mt="36">
                    <Title size="sm" message={messages.success} />
                    <Text message={messages.yourAccountHasBeen} />
                  </Box>
                  <Box mt="36">
                    <Button
                      title={messages.exploreTheCatalog}
                      to={redirectLink}
                      size="md"
                      color="brand"
                      rightIcon="rightArrowSmall"
                      fullWidthOnMobile
                      onClick={() => {
                        mixpanel.track(constants.analyticsEvents.newThankYouPage.goToCatalogClick, {
                          placement: 'upgrade success',
                        })
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Fragment>
                  <Header />
                  <OrderInfo isFetching={isFetching} items={billItems} total={total} />
                  {
                    isChargeInfoVisible && (
                      <Box mt="48">
                        {
                          Boolean(upgradeActivationMonth) && (
                            <Text
                              align="center"
                              message={{ ...messages.estimateNote, values: { upgradeActivationMonth } }}
                              html
                            />
                          )
                        }
                        {
                          Boolean(nextChargeMonth) && (
                            <Text
                              align="center"
                              message={{ ...messages.nextChargeNote, values: { nextChargeMonth } }}
                              html
                            />
                          )
                        }
                      </Box>
                    )
                  }
                  <Box mt="36">
                    <ButtonsInRow>
                      <Button
                        title={messages.continueToPayment}
                        size="md"
                        color="brand"
                        rightIcon="rightArrowSmall"
                        fullWidthOnMobile
                        dataTestId="modalContinueButton"
                        onClick={handleContinueClick}
                      />
                      <Box mt={isMobile && '24'} ml={!isMobile && '16'}>
                        <Button
                          title={messages.back}
                          size="md"
                          fullWidthOnMobile
                          dataTestId="modalBackButton"
                          onClick={onClose}
                        />
                      </Box>
                    </ButtonsInRow>
                  </Box>
                </Fragment>
              )
            }
          </ContentWithLoader>
        </Modal>
      </ModalContainer>
      <Overlay />
    </Portal>
  )
}

SelectedPlanSummaryModal.propTypes = {
  plan: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}


export default injectIntl(SelectedPlanSummaryModal)
